import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Users, Building2, Globe } from "lucide-react";

const AdminClients = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(q);
  const [country, setCountry] = useState(params.get("country") || "any");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    display_name: "", client_type: "individual",
    primary_email: "", primary_phone: "", country: "IN",
    notes: "", manual_client_number: "",
  });

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 400); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = { limit: 100 };
      if (debouncedQ) p.q = debouncedQ;
      if (country && country !== "any") p.country = country;
      const { data } = await api.get("/admin/clients", { params: p });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (err) {
      toast.error("Failed to load clients", { description: err?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, [debouncedQ, country]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const n = new URLSearchParams();
    if (q) n.set("q", q);
    if (country !== "any") n.set("country", country);
    setParams(n, { replace: true });
  }, [q, country, setParams]);

  const submitCreate = async () => {
    if (!form.display_name.trim()) return toast.error("Name is required");
    setCreating(true);
    try {
      const body = { ...form };
      if (!body.manual_client_number) delete body.manual_client_number;
      if (!body.primary_email) delete body.primary_email;
      if (!body.primary_phone) delete body.primary_phone;
      const { data } = await api.post("/admin/clients", body);
      toast.success("Client created", { description: data.display_number });
      setShowCreate(false);
      setForm({ display_name: "", client_type: "individual", primary_email: "", primary_phone: "", country: "IN", notes: "", manual_client_number: "" });
      navigate(`/admin/clients/${data.id}`);
    } catch (err) {
      toast.error("Could not create client", { description: err?.response?.data?.detail || "" });
    } finally { setCreating(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-clients-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="clients-page-title">Clients</h1>
            <p className="text-sm text-muted-foreground mt-1">Master client registry — every project, quote, invoice links here.</p>
          </div>
          <Button data-testid="new-client-btn" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New Client
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input data-testid="clients-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, number, email, phone…" className="pl-9" />
              </div>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="clients-country-filter" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any country</SelectItem>
                  <SelectItem value="IN">India</SelectItem>
                  <SelectItem value="US">USA</SelectItem>
                  <SelectItem value="CA">Canada</SelectItem>
                  <SelectItem value="AU">Australia</SelectItem>
                  <SelectItem value="GB">UK</SelectItem>
                  <SelectItem value="CH">Switzerland</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="pt-0" data-testid="clients-list">
            {loading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Loading clients…</div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="clients-empty-state">
                No clients yet. <button onClick={() => setShowCreate(true)} className="underline">Create the first one</button>.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
                    <tr>
                      <th className="text-left py-3 pr-3">Client</th>
                      <th className="text-left py-3 pr-3">Type</th>
                      <th className="text-left py-3 pr-3">Contact</th>
                      <th className="text-left py-3 pr-3">Country</th>
                      <th className="text-left py-3 pr-3">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-b last:border-b-0 hover:bg-slate-50">
                        <td className="py-3 pr-3">
                          <Link to={`/admin/clients/${c.id}`} className="font-medium hover:underline" data-testid={`client-link-${c.id}`}>{c.display_name}</Link>
                          <div className="text-xs text-muted-foreground font-mono">{c.display_number}</div>
                        </td>
                        <td className="py-3 pr-3">
                          {c.client_type === "company" ? <Building2 className="h-4 w-4 inline" /> : <Users className="h-4 w-4 inline" />}
                          <span className="ml-1 capitalize">{c.client_type}</span>
                        </td>
                        <td className="py-3 pr-3">
                          <div>{c.primary_email || "—"}</div>
                          <div className="text-xs text-muted-foreground">{c.primary_phone || ""}</div>
                        </td>
                        <td className="py-3 pr-3"><Globe className="h-3.5 w-3.5 inline mr-1 text-muted-foreground" />{c.country}</td>
                        <td className="py-3 pr-3 text-xs text-muted-foreground">{c.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pt-3 text-xs text-muted-foreground">{items.length} of {total} client{total === 1 ? "" : "s"}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-client-dialog">
          <DialogHeader><DialogTitle>New client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Display name *</Label>
              <Input data-testid="create-client-name" value={form.display_name} onChange={(e) => setForm({...form, display_name: e.target.value})} placeholder="Acme Homes Ltd or Priya Sharma" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.client_type} onValueChange={(v) => setForm({...form, client_type: v})}>
                  <SelectTrigger data-testid="create-client-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Select value={form.country} onValueChange={(v) => setForm({...form, country: v})}>
                  <SelectTrigger data-testid="create-client-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IN">India</SelectItem>
                    <SelectItem value="US">USA</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="AU">Australia</SelectItem>
                    <SelectItem value="GB">UK</SelectItem>
                    <SelectItem value="CH">Switzerland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Primary email</Label>
                <Input data-testid="create-client-email" type="email" value={form.primary_email} onChange={(e) => setForm({...form, primary_email: e.target.value})} />
              </div>
              <div>
                <Label>Primary phone</Label>
                <Input data-testid="create-client-phone" value={form.primary_phone} onChange={(e) => setForm({...form, primary_phone: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea data-testid="create-client-notes" rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
            </div>
            <div>
              <Label className="text-xs">Manual client number (super_admin only, for legacy imports)</Label>
              <Input data-testid="create-client-manual-number" value={form.manual_client_number} onChange={(e) => setForm({...form, manual_client_number: e.target.value})} placeholder="e.g. LEGACY-2020-001" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="submit-client-create" onClick={submitCreate} disabled={creating}>{creating ? "Creating…" : "Create client"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminClients;
